import type {QueryClient} from "@tanstack/react-query";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {uniqueUuid} from "@vesta/unique/unique";
import {z as zod} from "zod";
import {adminAuthenticationMethodValidator, type AdminAuthenticationMethod} from "~/domainModels/adminModels/adminAuthenticationMethod";
import {adminAuthenticationSessionValidator, type AdminAuthenticationSessionRow, type AdminAuthenticationSessionListRow} from "~/domainModels/adminModels/adminAuthenticationSession";
import {
    authenticationUserValidator,
    type AuthenticationUser,
    type AuthenticationUserRow,
} from "~/domainModels/adminModels/adminAuthenticationUser";
import {adminUserDetailValidator, type AdminUserDetail} from "~/domainModels/adminModels/adminUserDetails";
import {USER_DETAILS_QUERY_KEY} from "../user-profiles/route";

export const AUTHENTICATION_USERS_QUERY_KEY = uniqueUuid`a9db5aab-9719-486b-9bd3-fa87f9d64401`;

export const AUTHENTICATION_USER_AUTH_METHODS_QUERY_KEY = uniqueUuid`b8c7d6e5-f4a3-4b2c-9d1e-0f8a7b6c5d4e`;

export const AUTHENTICATION_USER_SESSIONS_QUERY_KEY = uniqueUuid`c9d8e7f6-a5b4-4c3d-8e2f-1a0b9c8d7e6f`;

export const AUTHENTICATION_ALL_SESSIONS_QUERY_KEY = uniqueUuid`d0e9f8a7-b6c5-4d3e-9f3a-2b1c0d9e8f7a`;

async function getAuthenticationUsers({
    intellsysBaseUrl,
    accessToken,
}: {
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Array<AuthenticationUser>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/authentication/users/get`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({}),
        },
        zod.array(authenticationUserValidator),
    );
    if (result.success !== true) {
        throw new Error("Failed to fetch authentication users");
    }
    return result.data;
}

async function getUserDetailsWithCaching({
    queryClient,
    intellsysBaseUrl,
    accessToken,
}: {
    queryClient: QueryClient;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Array<AdminUserDetail>> {
    return queryClient.ensureQueryData<Array<AdminUserDetail>>({
        queryKey: [USER_DETAILS_QUERY_KEY],
        queryFn: async () => {
            const result = await robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/admin/user-details/get`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({}),
                },
                zod.array(adminUserDetailValidator),
            );
            if (result.success !== true) {
                throw new Error("Failed to fetch user details");
            }
            return result.data;
        },
    });
}

function mergeAuthenticationUserRows({
    users,
    userDetails,
}: {
    users: Array<AuthenticationUser>;
    userDetails: Array<AdminUserDetail>;
}): Array<AuthenticationUserRow> {
    const userDetailsByUserId = new Map(userDetails.map((detail) => [detail.userId, detail]));

    return users.map((user) => {
        const detail = userDetailsByUserId.get(user.id);
        return {
            ...user,
            name: detail?.name ?? null,
            email: detail?.email ?? null,
            phoneNumber: detail?.phoneNumber ?? null,
        };
    });
}

export async function getAuthenticationUserRowsWithCaching({
    queryClient,
    intellsysBaseUrl,
    accessToken,
}: {
    queryClient: QueryClient;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Array<AuthenticationUserRow>> {
    return queryClient.ensureQueryData<Array<AuthenticationUserRow>>({
        queryKey: [AUTHENTICATION_USERS_QUERY_KEY],
        queryFn: async () => {
            const [users, userDetails] = await Promise.all([
                getAuthenticationUsers({intellsysBaseUrl, accessToken}),
                getUserDetailsWithCaching({queryClient, intellsysBaseUrl, accessToken}),
            ]);
            return mergeAuthenticationUserRows({users, userDetails});
        },
    });
}

export async function getAuthenticationUserWithCaching({
    intellsysBaseUrl,
    accessToken,
    userId,
}: {
    intellsysBaseUrl: string;
    accessToken: string;
    userId: Uuid;
}): Promise<AuthenticationUser> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/authentication/users/get-one`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({userId}),
        },
        authenticationUserValidator,
    );
    if (result.success !== true) {
        throw result.error;
    }
    return result.data;
}

export async function getAuthenticationUserProfileWithCaching({
    queryClient,
    intellsysBaseUrl,
    accessToken,
    userId,
}: {
    queryClient: QueryClient;
    intellsysBaseUrl: string;
    accessToken: string;
    userId: Uuid;
}): Promise<AdminUserDetail | null> {
    const userDetails = await getUserDetailsWithCaching({
        queryClient,
        intellsysBaseUrl,
        accessToken,
    });
    return userDetails.find((detail) => detail.userId === userId) ?? null;
}

export async function getAuthenticationMethodsForUserWithCaching({
    queryClient,
    intellsysBaseUrl,
    accessToken,
    userId,
}: {
    queryClient: QueryClient;
    intellsysBaseUrl: string;
    accessToken: string;
    userId: Uuid;
}): Promise<Array<AdminAuthenticationMethod>> {
    return queryClient.ensureQueryData<Array<AdminAuthenticationMethod>>({
        queryKey: [AUTHENTICATION_USER_AUTH_METHODS_QUERY_KEY, userId],
        queryFn: async () => {
            const result = await robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/authentication/auth-methods/get`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({userId}),
                },
                zod.unknown(),
            );
            if (result.success !== true) {
                throw new Error(`Failed to fetch authentication methods: ${result.error.message}`);
            }

            const parsedMethods = zod.array(adminAuthenticationMethodValidator).safeParse(result.data);
            if (parsedMethods.success !== true) {
                console.error("Authentication methods validation failed:", parsedMethods.error);
                throw new Error("Failed to validate authentication methods response");
            }

            return parsedMethods.data;
        },
    });
}

export async function getAuthenticationSessionsForUserWithCaching({
    queryClient,
    intellsysBaseUrl,
    accessToken,
    userId,
}: {
    queryClient: QueryClient;
    intellsysBaseUrl: string;
    accessToken: string;
    userId: Uuid;
}): Promise<Array<AdminAuthenticationSessionRow>> {
    return queryClient.ensureQueryData<Array<AdminAuthenticationSessionRow>>({
        queryKey: [AUTHENTICATION_USER_SESSIONS_QUERY_KEY, userId],
        queryFn: async () => {
            const result = await robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/authentication/sessions/get`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({userId}),
                },
                zod.array(adminAuthenticationSessionValidator),
            );
            if (result.success !== true) {
                throw new Error(`Failed to fetch authentication sessions: ${result.error.message}`);
            }
            return result.data.map((session) => ({
                ...session,
                id: session.oidcSessionUid,
            }));
        },
    });
}

export async function getAllAuthenticationSessionsWithCaching({
    queryClient,
    intellsysBaseUrl,
    accessToken,
}: {
    queryClient: QueryClient;
    intellsysBaseUrl: string;
    accessToken: string;
}): Promise<Array<AdminAuthenticationSessionListRow>> {
    return queryClient.ensureQueryData<Array<AdminAuthenticationSessionListRow>>({
        queryKey: [AUTHENTICATION_ALL_SESSIONS_QUERY_KEY],
        queryFn: async () => {
            const [sessionsResult, userDetails] = await Promise.all([
                robustJsonResultClientFetch(
                    `${intellsysBaseUrl}/sub-system/pluto/authentication/sessions/get-all`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${accessToken}`,
                        },
                        body: JSON.stringify({}),
                    },
                    zod.array(adminAuthenticationSessionValidator),
                ),
                getUserDetailsWithCaching({queryClient, intellsysBaseUrl, accessToken}),
            ]);

            if (sessionsResult.success !== true) {
                throw new Error(`Failed to fetch authentication sessions: ${sessionsResult.error.message}`);
            }

            const userDetailsByUserId = new Map(userDetails.map((detail) => [detail.userId, detail]));

            return sessionsResult.data.map((session) => {
                const detail = session.userId === null ? undefined : userDetailsByUserId.get(session.userId);
                return {
                    ...session,
                    id: session.oidcSessionUid,
                    name: detail?.name ?? null,
                    email: detail?.email ?? null,
                    phoneNumber: detail?.phoneNumber ?? null,
                };
            });
        },
    });
}
